<template>
  <div
    role="alert"
    class="persistent-banner fixed z-2000 px-5 py-4 md:py-5 left-0 right-0 mx-auto transition-all duration-300 ease-in-out"
    :class="[
      'bottom-0 w-full',
      'md:bottom-8 md:w-[calc(100%-3rem)] md:max-w-[850px] md:rounded-2xl',
      'shadow-2xl overflow-hidden animate-slide-up',
    ]"
  >
    <!-- Premium background decorations -->
    <div
      class="absolute -top-12 -right-12 w-40 h-40 bg-white/10 rounded-full blur-2xl pointer-events-none"
    ></div>
    <div
      class="absolute -bottom-12 -left-12 w-40 h-40 bg-black/10 rounded-full blur-2xl pointer-events-none"
    ></div>

    <div
      class="relative z-10 flex flex-col md:flex-row items-center justify-between gap-5 md:gap-6"
    >
      <div class="flex items-center gap-4 flex-1 w-full">
        <div class="flex items-center justify-center w-11 h-11 rounded-xl bg-white/20 shrink-0">
          <v-icon
            icon="fas fa-exclamation-triangle"
            class="text-white text-xl"
            aria-hidden="true"
          />
        </div>
        <div class="flex-1 min-w-0">
          <p
            class="medium-font text-white text-sm md:text-[15px] leading-relaxed"
            v-html="message"
          ></p>
        </div>
      </div>

      <div
        class="flex flex-col md:flex-row items-center gap-3 md:gap-4 w-full md:w-auto shrink-0 mt-0"
      >
        <v-btn
          :to="actionRoute"
          variant="flat"
          class="action-button w-full md:w-auto md:flex-none bold-font rounded-xl h-11 px-6 text-sm transform transition active:scale-95 shadow-lg"
        >
          {{ actionLabel }}
        </v-btn>

        <!-- Desktop Close icon -->
        <v-btn
          variant="text"
          icon
          density="comfortable"
          class="hidden! md:inline-flex! text-white/80! hover:text-white! hover:bg-white/10! transition-colors"
          title="Cerrar"
          @click="onDismiss"
        >
          <v-icon
            icon="fas fa-times"
            size="small"
            color="white"
            class="text-white!"
          />
        </v-btn>

        <!-- Mobile Dismiss text -->
        <v-btn
          variant="outlined"
          class="flex! md:hidden! w-full text-white/90! medium-font text-sm uppercase tracking-wider h-11"
          @click="onDismiss"
        >
          Cerrar
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { RouteLocationRaw } from 'vue-router';

  interface Props {
    message: string;
    actionLabel: string;
    actionRoute: RouteLocationRaw;
    onDismiss: () => void;
  }

  defineProps<Props>();
</script>

<style scoped lang="scss">
  .persistent-banner {
    background: #d32f2f;
    border: 1px solid rgba(255, 255, 255, 0.2);
    box-shadow: 0 20px 50px -12px rgba(244, 67, 54, 0.6);
    backdrop-filter: blur(8px);

    @media (max-width: 767px) {
      border-left: none;
      border-right: none;
      border-bottom: none;
    }
  }

  .action-button {
    background-color: var(--color-white) !important;
    color: var(--color-red) !important;
    letter-spacing: 0.025em;
  }

  /* Style for highlighted text (strong tags) */
  .persistent-banner :deep(strong) {
    font-family: var(--font-poppins-semi-bold, 'Poppins Semi Bold');
    font-weight: 600;
    color: white;
    background: rgba(255, 255, 255, 0.15);
    padding: 0 6px;
    border-radius: 6px;
    margin: 0 1px;
    white-space: nowrap;
    border: 1px solid rgba(255, 255, 255, 0.2);
    display: inline-block;
    vertical-align: middle;
    line-height: 1.4;
  }

  .animate-slide-up {
    animation: slide-up 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  }

  @keyframes slide-up {
    from {
      transform: translateY(150%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
</style>
